export type AnalyticsConsent = "unknown" | "accepted" | "rejected";

const COOKIE_NAME = "aiia_cookie_consent";
const COOKIE_VERSION = "v2";
const LEGACY_REJECTION = "v1:rejected";
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
const CONSENT_CHANNEL = "aiia-analytics-consent";
const COOKIE_CHECK_INTERVAL_MS = 1_000;
const listeners = new Set<(consent: AnalyticsConsent) => void>();

let sessionConsent: AnalyticsConsent = "unknown";
let usesMemoryFallback = false;

const isBrowser = (): boolean => typeof document !== "undefined";

const readCookie = (): AnalyticsConsent => {
  if (!isBrowser()) return "unknown";
  try {
    const prefix = `${COOKIE_NAME}=`;
    const raw = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix))
      ?.slice(prefix.length);
    if (raw === `${COOKIE_VERSION}:accepted`) return "accepted";
    if (raw === `${COOKIE_VERSION}:rejected`) return "rejected";
    if (raw === LEGACY_REJECTION) return "rejected";
  } catch {
    /* Cookie access can be blocked. Unknown keeps analytics disabled. */
  }
  return "unknown";
};

export const getAnalyticsConsent = (): AnalyticsConsent =>
  usesMemoryFallback ? sessionConsent : readCookie();

export const hasAnalyticsConsent = (): boolean =>
  getAnalyticsConsent() === "accepted";

const expireCookie = (name: string, domain?: string): void => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domainPart = domain ? `; Domain=${domain}` : "";
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}${domainPart}`;
};

export const clearClarityCookies = (): void => {
  if (!isBrowser()) return;
  try {
    const clarityCookieNames = document.cookie
      .split(";")
      .map((part) => part.trim().split("=")[0])
      .filter((name) => name === "_clck" || name === "_clsk");
    for (const name of clarityCookieNames) {
      expireCookie(name);
      if (
        window.location.hostname === "aiinterviewagents.com" ||
        window.location.hostname.endsWith(".aiinterviewagents.com")
      ) {
        expireCookie(name, ".aiinterviewagents.com");
      }
    }
  } catch {
    /* Consent changes must never break the app. */
  }
};

const clearOptionalAnalyticsCookies = (): void => {
  if (!isBrowser()) return;
  try {
    const optionalCookieNames = document.cookie
      .split(";")
      .map((part) => part.trim().split("=")[0])
      .filter(
        (name) =>
          name === "_clck" ||
          name === "_clsk" ||
          name === "_ga" ||
          name.startsWith("_ga_") ||
          name === "_gid" ||
          name === "_gat" ||
          name.startsWith("_gat_") ||
          name === "aiia_visitor_id"
      );
    for (const name of optionalCookieNames) {
      expireCookie(name);
      if (
        window.location.hostname === "aiinterviewagents.com" ||
        window.location.hostname.endsWith(".aiinterviewagents.com")
      ) {
        expireCookie(name, ".aiinterviewagents.com");
      }
    }
  } catch {
    /* Consent changes must never break the app. */
  }
};

const notifyListeners = (consent: AnalyticsConsent): void => {
  if (consent !== "accepted") clearOptionalAnalyticsCookies();
  for (const listener of listeners) {
    try {
      listener(consent);
    } catch {
      /* One analytics integration must not block the others or the UI. */
    }
  }
};

let observedConsent: AnalyticsConsent = getAnalyticsConsent();

const syncConsentFromCookie = (overrideMemoryFallback = false): void => {
  if (!isBrowser()) return;
  if (usesMemoryFallback && !overrideMemoryFallback) return;
  usesMemoryFallback = false;
  sessionConsent = "unknown";
  const consent = readCookie();
  if (consent === observedConsent) return;
  observedConsent = consent;
  notifyListeners(consent);
};

const consentChannel =
  isBrowser() && typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(CONSENT_CHANNEL)
    : null;

if (consentChannel) {
  consentChannel.addEventListener("message", (event) => {
    if (event.data !== "preference-changed") return;
    syncConsentFromCookie(true);
  });
}

export const setAnalyticsConsent = (
  consent: Exclude<AnalyticsConsent, "unknown">
): void => {
  if (!isBrowser()) return;

  // Keep an explicit choice for this page session even when cookie writes are
  // blocked. A reload then safely returns to unknown and tracking stays off.
  sessionConsent = consent;
  usesMemoryFallback = true;
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_NAME}=${COOKIE_VERSION}:${consent}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
    if (readCookie() === consent) {
      usesMemoryFallback = false;
      sessionConsent = "unknown";
    }
  } catch {
    /* The in-memory explicit choice remains effective for this session. */
  }

  observedConsent = consent;
  notifyListeners(consent);
  consentChannel?.postMessage("preference-changed");
};

export const subscribeToAnalyticsConsent = (
  listener: (consent: AnalyticsConsent) => void
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

if (isBrowser() && getAnalyticsConsent() === "rejected") {
  clearOptionalAnalyticsCookies();
}

if (isBrowser()) {
  window.setInterval(syncConsentFromCookie, COOKIE_CHECK_INTERVAL_MS);
  window.addEventListener("focus", () => syncConsentFromCookie());
  document.addEventListener("visibilitychange", () => syncConsentFromCookie());
}
